/**
 * Source marks for the Connections cards.
 *
 * **A brand mark is not a decorative glyph, which is why these earn their
 * place where `icons.tsx`'s rule would refuse them.** The seven section icons
 * are hand-drawn because seven line glyphs do not justify a dependency; these
 * three identify *third-party systems*, and an approximation of somebody's
 * trademark drawn from memory reads as wrong in a way a hand-drawn phone
 * outline does not. `CLAUDE.md` records exactly that failure from the
 * architecture skill: absent real logos, the model drew its own.
 *
 * **Full colour, reversing the original call — Nilay, 10 Sep (CN-5).**
 *
 * These were monochrome on the grounds that Snapchat's `#FFFC00` and ClickUp's
 * pink would fight a palette whose premise is *mint carries identity, blue
 * carries data*, and that `DECISIONS.md` forbids an invented hue outright.
 *
 * The counter-argument, and why it wins here: **a vendor's own trademark is not
 * an invented hue.** It is a fixed external fact, like a country flag in the
 * channel list, and on a pipeline-health card the mark's whole job is instant
 * recognition of *which third party is broken*. A tinted grey Snapchat ghost is
 * a worse card than a yellow Snapchat logo.
 *
 * ⚠️ **This is a recorded design-system deviation, not a colour tweak.** It puts
 * three non-palette hues on `/connections`. It is confined to these three marks
 * — never a series colour, never a status, never a surface — and it is written
 * into `design-system/DECISIONS.md` so the next reader does not revert it on
 * sight of the palette rule.
 *
 * Paths from simple-icons (CC0). The marks themselves remain their owners'
 * trademarks, used here only to name the system each card reports on.
 *
 * `thumbnails` is our own pipeline rather than a vendor, so it carries the
 * **DigitalOcean** mark — Spaces is where it writes, and that is the honest
 * answer to "what is this talking to". Snapchat's mark would imply a second
 * Snap integration.
 */
type MarkProps = { className?: string; size?: number }

function Mark({
  path,
  label,
  brand,
  className,
  size = 18,
}: MarkProps & { path: string; label: string; brand: string }) {
  return (
    <svg
      // Never the accessible name: the card's heading carries it. An icon that
      // labels itself and sits beside its own label reads twice to a screen
      // reader.
      aria-hidden="true"
      focusable="false"
      role="img"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      // The vendor's own brand colour, per CN-5. `brand` is passed by each
      // exported mark rather than inherited, so a mark can never silently pick
      // up a text colour and read as disabled.
      fill={brand}
      className={className}
    >
      <title>{label}</title>
      <path d={path} />
    </svg>
  )
}

const SNAPCHAT_PATH =
  'M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z'

/** Snap yellow. */
export const SnapchatMark = (props: MarkProps) => (
  <Mark {...props} path={SNAPCHAT_PATH} label="Snapchat" brand="#FFFC00" />
)

const CLICKUP_PATH =
  'M2 18.439l3.69-2.828c1.961 2.56 4.044 3.739 6.363 3.739 2.307 0 4.33-1.166 6.203-3.704L22 18.405C19.298 22.065 15.941 24 12.053 24 8.178 24 4.788 22.078 2 18.439zM12.04 6.15l-6.568 5.66-3.036-3.52L12.055 0l9.543 8.296-3.05 3.509z'

/** ClickUp pink — the flat brand colour, since their mark is a gradient we
 *  cannot reproduce in a single path. */
export const ClickUpMark = (props: MarkProps) => (
  <Mark {...props} path={CLICKUP_PATH} label="ClickUp" brand="#FA12E3" />
)

const DIGITALOCEAN_PATH =
  'M12.04 0C5.408-.02.005 5.37.005 11.992h4.638c0-4.923 4.882-8.731 10.064-6.855a6.95 6.95 0 014.147 4.148c1.889 5.177-1.924 10.055-6.84 10.064v-4.61H7.391v4.623h4.61V24c7.86 0 13.967-7.588 11.397-15.83-1.115-3.59-3.985-6.446-7.575-7.575A12.8 12.8 0 0012.039 0zM7.39 19.362H3.828v3.564H7.39zm-3.563 0v-2.978H.85v2.978z'

/** DigitalOcean blue. */
export const DigitalOceanMark = (props: MarkProps) => (
  <Mark {...props} path={DIGITALOCEAN_PATH} label="DigitalOcean" brand="#0080FF" />
)

/** Which mark belongs to which pipeline. */
export const SOURCE_MARKS: Record<string, (props: MarkProps) => JSX.Element> = {
  snap: SnapchatMark,
  clickup: ClickUpMark,
  thumbnails: DigitalOceanMark,
}
